import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStatusLabel } from './StatusBadge';
import { FolderOpen, Camera, Search, Menu, Sun, Moon, Monitor, LogOut, User, MapPin, X, ChevronRight } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import DashboardMetrics from '../DashboardMetrics';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { isNotificationEnabled, requestNotificationPermission } from '../../services/notificationService';
import { getAllProjects } from '../../services/projectService';
import { getPhotosByProject } from '../../services/photoService';
import { Project } from '../../types';
import { hapticLight } from '../../utils/haptics';

interface Tab {
  id: string;
  label: string;
  icon: React.FC<{ size?: number; strokeWidth?: number; className?: string }>;
}

const tabs: Tab[] = [
  { id: 'projekt', label: 'Projekt', icon: FolderOpen },
  { id: 'kamera', label: 'Foton', icon: Camera },
  { id: 'sok', label: 'Sök', icon: Search },
  { id: 'mer', label: 'Mer', icon: Menu },
];

export function TabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(isNotificationEnabled());

  // Project data for camera picker and search
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Track photo count for current project
  const [photoCount, setPhotoCount] = useState(0);
  const projectMatch = location.pathname.match(/\/projects\/([^/]+)/);
  const currentProjectId = projectMatch?.[1];

  useEffect(() => {
    if (!currentProjectId) {
      setPhotoCount(0);
      return;
    }
    getPhotosByProject(currentProjectId).then(photos => setPhotoCount(photos.length)).catch(() => {});
  }, [currentProjectId]);

  const getActiveTab = () => {
    if (/\/projects\/[^/]+\/photos/.test(location.pathname)) {
      return 'kamera';
    }
    if (location.pathname === '/' || location.pathname.startsWith('/projects') || location.pathname.startsWith('/ai-analysis')) {
      return 'projekt';
    }
    return '';
  };

  const activeTab = getActiveTab();

  // Load projects when camera or search sheet opens
  const loadProjects = async () => {
    if (projectsLoaded) return;
    try {
      const fetched = await getAllProjects();
      setProjects(fetched);
      setProjectsLoaded(true);
    } catch (err) {
      console.error('Error loading projects for TabBar:', err);
    }
  };

  // Auto-focus search input when search opens
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 300);
    } else {
      setSearchTerm('');
    }
  }, [searchOpen]);

  const handleTabPress = (tab: Tab) => {
    hapticLight();
    switch (tab.id) {
      case 'projekt':
        navigate('/');
        break;
      case 'kamera': {
        // If already on a project page, go straight to its photos
        const projectMatch = location.pathname.match(/\/projects\/([^/]+)/);
        if (projectMatch) {
          navigate(`/projects/${projectMatch[1]}/photos`);
        } else {
          // Show project picker
          loadProjects();
          setCameraOpen(true);
        }
        break;
      }
      case 'sok':
        loadProjects();
        setSearchOpen(true);
        break;
      case 'mer':
        setMoreOpen(true);
        break;
    }
  };

  const filteredProjects = searchTerm
    ? projects.filter(p => {
        const term = searchTerm.toLowerCase();
        return (
          p.address?.street?.toLowerCase().includes(term) ||
          p.address?.city?.toLowerCase().includes(term) ||
          p.contact?.name?.toLowerCase().includes(term) ||
          p.status?.toLowerCase().includes(term)
        );
      })
    : projects;

  // Search results use the same wording as the badges on the cards they lead
  // to. This used to be a private copy that said "Bostad" / "Värderad" while
  // the cards said "Ny bostad" / "Klar för hämtning".
  const statusLabel = (status: string) => getStatusLabel(status);

  const themeOptions: { value: 'light' | 'dark' | 'system'; icon: React.FC<any>; label: string }[] = [
    { value: 'light', icon: Sun, label: 'Ljust' },
    { value: 'dark', icon: Moon, label: 'Mörkt' },
    { value: 'system', icon: Monitor, label: 'Auto' },
  ];

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="absolute inset-0 bg-[var(--bg-surface)]/80 backdrop-blur-xl border-t border-[var(--border-default)]" />

        <div className="relative flex items-end justify-around h-[49px]">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabPress(tab)}
                className={`flex flex-col items-center justify-center flex-1 h-full pt-1.5 pb-0.5 transition-colors ${
                  active
                    ? 'text-accent-600 dark:text-accent-400'
                    : 'text-[var(--text-tertiary)]'
                }`}
                aria-label={tab.label}
              >
                <div className="relative">
                  <Icon size={22} strokeWidth={active ? 2.2 : 1.5} />
                  {tab.id === 'kamera' && photoCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-accent-600 text-white text-[12px] font-bold flex items-center justify-center">
                      {photoCount}
                    </span>
                  )}
                </div>
                <span className="text-[12px] font-medium mt-0.5 leading-tight">
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Camera — project picker to navigate to photos page */}
      <BottomSheet isOpen={cameraOpen} onClose={() => setCameraOpen(false)} title="Välj projekt att fotografera">
        <div className="px-4 pb-4">
          {!projectsLoaded ? (
            <div className="py-8 text-center text-[var(--text-tertiary)] text-sm">Laddar projekt...</div>
          ) : (
            <div data-sheet-scroll className="space-y-1 max-h-[50vh] overflow-y-auto -mx-4 px-4">
              {projects
                .filter(p => p.status !== 'avslutad' && p.status !== 'avbruten')
                .slice(0, 10)
                .map(project => (
                  <button
                    key={project.id}
                    onClick={() => {
                      setCameraOpen(false);
                      navigate(`/projects/${project.id}/photos`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl active:bg-[var(--bg-surface-alt)] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center flex-shrink-0">
                      <Camera size={16} className="text-accent-600 dark:text-accent-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                        {project.address?.street}
                      </p>
                      <p className="text-xs text-[var(--text-tertiary)] truncate">
                        {project.address?.city} · {statusLabel(project.status)}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-[var(--text-tertiary)] flex-shrink-0" />
                  </button>
                ))}
              {projects.filter(p => p.status !== 'avslutad' && p.status !== 'avbruten').length === 0 && (
                <div className="py-8 text-center text-[var(--text-tertiary)] text-sm">Inga aktiva projekt</div>
              )}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Search — global project search */}
      <BottomSheet isOpen={searchOpen} onClose={() => setSearchOpen(false)} title="Sök projekt">
        <div className="px-4 pb-4">
          {/* Search input */}
          <div className="relative mb-3">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Adress, kundnamn..."
              className="w-full h-10 pl-9 pr-8 rounded-lg bg-[var(--bg-surface-alt)] text-[15px] text-[var(--text-primary)] placeholder-[var(--text-secondary)] outline-none focus:ring-2 focus:ring-[var(--ring-accent)] transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full bg-[var(--text-tertiary)]/30"
                aria-label="Rensa"
              >
                <X size={12} className="text-[var(--text-secondary)]" />
              </button>
            )}
          </div>

          {/* Results */}
          {!projectsLoaded ? (
            <div className="py-6 text-center text-[var(--text-tertiary)] text-sm">Laddar...</div>
          ) : (
            <div data-sheet-scroll className="space-y-1 max-h-[50vh] overflow-y-auto -mx-4 px-4">
              {filteredProjects.length === 0 ? (
                <div className="py-6 text-center text-[var(--text-tertiary)] text-sm">
                  {searchTerm ? 'Inga träffar' : 'Inga projekt'}
                </div>
              ) : (
                filteredProjects.slice(0, 15).map(project => (
                  <button
                    key={project.id}
                    onClick={() => {
                      setSearchOpen(false);
                      navigate(`/projects/${project.id}`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl active:bg-[var(--bg-surface-alt)] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[var(--bg-surface-alt)] flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-[var(--text-secondary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                        {project.address?.street}
                      </p>
                      <p className="text-xs text-[var(--text-tertiary)] truncate">
                        {project.address?.city} · {project.contact?.name || statusLabel(project.status)}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-[var(--text-tertiary)] flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* "Mer" sheet */}
      <BottomSheet isOpen={moreOpen} onClose={() => setMoreOpen(false)} title="Inställningar">
        <div className="px-4 pb-4 space-y-4">
          {/* User info */}
          <div className="flex items-center gap-3 py-2">
            <div className="w-10 h-10 rounded-full bg-[var(--bg-surface-alt)] flex items-center justify-center">
              <User size={18} className="text-[var(--text-secondary)]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user?.email}</p>
            </div>
          </div>

          {/* Statistics: moved here from the top of the home list so the
              list starts with today's and this week's work instead. */}
          <div>
            <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-2">Statistik</p>
            {moreOpen && <DashboardMetrics embedded className="mb-0" />}
          </div>

          {/* Theme toggle */}
          <div>
            <p className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-2">Utseende</p>
            <div className="flex items-center bg-[var(--bg-surface-alt)] rounded-xl p-1">
              {themeOptions.map(opt => {
                const Icon = opt.icon;
                const isActive = theme === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm'
                        : 'text-[var(--text-tertiary)]'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notifications */}
          <div>
            <p className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-2">Notifikationer</p>
            {notificationsEnabled ? (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--bg-surface-alt)]">
                <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
                <span className="text-sm text-[var(--text-primary)]">Notifikationer aktiva</span>
              </div>
            ) : (
              <button
                onClick={async () => {
                  const result = await requestNotificationPermission();
                  setNotificationsEnabled(result.enabled);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-[var(--bg-surface-alt)] active:bg-[var(--bg-surface-hover)] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--text-tertiary)]"><path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5" /><path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                <span className="text-sm text-[var(--text-primary)]">Aktivera notifikationer</span>
              </button>
            )}
          </div>

          {/* Sign out */}
          <button
            onClick={() => { setMoreOpen(false); signOut(); }}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 active:bg-red-50 dark:active:bg-red-900/20 transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Logga ut</span>
          </button>
        </div>
      </BottomSheet>
    </>
  );
}
